'use client';

import type { FC } from 'react';
import { forwardRef } from 'react';
import { cn } from '../../utils/cn';
import { useHaptic } from '../../hooks/useHaptic';

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  className?: string;
}

export const Switch: FC<SwitchProps> = forwardRef<HTMLInputElement, SwitchProps>(
  ({ checked, defaultChecked, disabled, onChange, className }, ref) => {
    const { impact } = useHaptic();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      impact('light');
      onChange?.(e.target.checked);
    };

    return (
      <label className={cn('group relative inline-block w-11 h-7 cursor-pointer', className)}>
        <input
          ref={ref}
          type="checkbox"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={handleChange}
          className="peer opacity-0 w-0 h-0"
        />
        <span
          className={cn(
            'absolute cursor-pointer inset-0 bg-gray-2 transition-all duration-[0.4s] rounded-full',
            'before:absolute before:content-[\'\'] before:h-5 before:w-5 before:start-1 before:bottom-1 before:bg-white before:transition-all before:duration-[0.4s] before:rounded-full',
            'peer-checked:bg-accent-blue peer-focus:shadow-[0_0_1px_var(--color-accent-blue)] peer-checked:before:translate-x-4 rtl:peer-checked:before:-translate-x-4 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed',
            'group-active:shadow-[inset_0_0_0_9999px_rgba(255,255,255,0.08)] peer-disabled:group-active:shadow-none',
          )}
        />
      </label>
    );
  },
);

Switch.displayName = 'Switch';
